import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';
import { ensureFreshApiToken } from '../auth/msal';

/**
 * Firm requirement library surface (T-0141 / E11-F01 browse + E11-F02
 * curation). Backs Firm settings -> Requirement library.
 *
 * Contract: `docs/requirement-library/technical/api-requirement-library.md`,
 * implemented in `apps/api/src/routes/requirement-library.ts` and
 * `apps/api/src/services/requirement-library.ts` (T-0136 read, T-0137
 * curation). All routes are firm-root under `/api/v1/requirement-library`.
 *
 *   - Reads are open to every signed-in firm user and return published
 *     content only.
 *   - Draft reads and every write need the template curation authority.
 *   - No request carries a prefix or a number: the server assigns both
 *     (amendment 01, Decision 32).
 *   - Draft writes carry `base_revision` (the `draft_revision` last seen).
 *     A stale value is a `conflict` whose `details.draft` is the current
 *     draft; see {@link staleDraftFromError}.
 */

const BASE = '/api/v1/requirement-library';

// ---------------------------------------------------------------------------
// Read shapes
// ---------------------------------------------------------------------------

export const LibraryModuleSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  status: z.string(),
  current_version: z.number().int(),
  published_at: z.string().nullable(),
  item_count: z.number().int(),
  /** Present for curators only. */
  has_draft: z.boolean().optional(),
  /** A rename waiting in the draft (curators only); takes effect on publish. */
  pending_name: z.string().nullable().optional(),
});
export type LibraryModuleSummary = z.infer<typeof LibraryModuleSummarySchema>;

export const LibraryCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  module_count: z.number().int(),
  item_count: z.number().int(),
  modules: z.array(LibraryModuleSummarySchema),
});
export type LibraryCategory = z.infer<typeof LibraryCategorySchema>;

export const LibraryTreeSchema = z.object({
  library_revision: z.string(),
  /** True when the caller holds the template curation authority. */
  can_curate: z.boolean(),
  module_count: z.number().int(),
  item_count: z.number().int(),
  categories: z.array(LibraryCategorySchema),
});
export type LibraryTree = z.infer<typeof LibraryTreeSchema>;

/**
 * Labelled fields kept beside a requirement's wording rather than in it
 * (migration 0144): the register metadata the Massachusetts import appended
 * to each requirement, moved out by "Tidy appended metadata" or split off
 * by the spreadsheet import. Null for most requirements.
 */
export const LibraryMetadataSchema = z.object({
  tag: z.string().optional(),
  live: z.string().optional(),
  basis: z.string().optional(),
  proof: z.string().optional(),
  pending: z.string().optional(),
});
export type LibraryMetadata = z.infer<typeof LibraryMetadataSchema>;

/** The fields in the order the source writes them, with the source's labels. */
export const LIBRARY_METADATA_LABELS: ReadonlyArray<[keyof LibraryMetadata, string]> = [
  ['tag', 'Tag'],
  ['live', 'Live'],
  ['basis', 'Basis'],
  ['proof', 'Proof'],
  ['pending', 'Pending'],
];

/** The fields a requirement carries, labelled and in order; empty when it has none. */
export function metadataEntries(
  metadata: LibraryMetadata | null | undefined,
): Array<{ field: keyof LibraryMetadata; label: string; value: string }> {
  if (!metadata) return [];
  return LIBRARY_METADATA_LABELS.flatMap(([field, label]) => {
    const value = metadata[field];
    return value ? [{ field, label, value }] : [];
  });
}

export const LibraryItemSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  v1_number: z.string().nullable(),
  module_id: z.string(),
  module_name: z.string(),
  category_id: z.string(),
  category_name: z.string(),
  subcategory: z.string(),
  text: z.string(),
  sort_order: z.number(),
  source_kind: z.string(),
  metadata: LibraryMetadataSchema.nullable().optional(),
  version: z.number().int(),
  status: z.string(),
});
export type LibraryItem = z.infer<typeof LibraryItemSchema>;

export const LibraryItemsSchema = z.object({
  library_revision: z.string(),
  count: z.number().int(),
  items: z.array(LibraryItemSchema),
});
export type LibraryItems = z.infer<typeof LibraryItemsSchema>;

export const ModuleVersionSchema = z.object({
  version: z.number().int(),
  module_name: z.string(),
  change_note: z.string().nullable(),
  item_count: z.number().int(),
  published_at: z.string(),
  published_by: z.object({ id: z.string(), name: z.string().nullable() }),
  is_current: z.boolean(),
});
export type ModuleVersion = z.infer<typeof ModuleVersionSchema>;

export const ModuleVersionsSchema = z.object({
  module: z.object({
    id: z.string(),
    name: z.string(),
    prefix: z.string(),
    status: z.string(),
    current_version: z.number().int(),
  }),
  versions: z.array(ModuleVersionSchema),
});
export type ModuleVersions = z.infer<typeof ModuleVersionsSchema>;

export const ModuleContentItemSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  subcategory: z.string(),
  text: z.string(),
  sort_order: z.number(),
  source_kind: z.string(),
  source_ref: z.unknown().optional(),
  metadata: LibraryMetadataSchema.nullable().optional(),
  status: z.string(),
});
export type ModuleContentItem = z.infer<typeof ModuleContentItemSchema>;

export const ModuleContentSchema = z.object({
  module: z.object({
    id: z.string(),
    name: z.string(),
    prefix: z.string(),
    status: z.string(),
    current_version: z.number().int(),
    category: z.object({ id: z.string(), name: z.string() }),
  }),
  version: z
    .object({
      version: z.number().int(),
      module_name: z.string(),
      change_note: z.string().nullable(),
      item_count: z.number().int(),
      published_at: z.string(),
      // The module route returns the publisher's id, not the { id, name } pair.
      published_by: z.unknown(),
      is_current: z.boolean(),
    })
    .nullable(),
  items: z.array(ModuleContentItemSchema),
});
export type ModuleContent = z.infer<typeof ModuleContentSchema>;

const DiffEntrySchema = z.object({
  item_id: z.string(),
  number: z.string(),
  subcategory: z.string(),
  text: z.string(),
});
const DiffRewordedSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  from: z.object({ subcategory: z.string(), text: z.string() }),
  to: z.object({ subcategory: z.string(), text: z.string() }),
});

export const VersionDiffSchema = z.object({
  module_id: z.string(),
  from: z.number().int(),
  to: z.number().int(),
  counts: z.object({
    added: z.number(),
    reworded: z.number(),
    retired: z.number(),
    renamed: z.number().optional(),
  }),
  added: z.array(DiffEntrySchema),
  reworded: z.array(DiffRewordedSchema),
  retired: z.array(DiffEntrySchema),
  /** The module's name in each version, when it differs between them. */
  renamed: z.object({ from: z.string(), to: z.string() }).nullable().optional(),
});
export type VersionDiff = z.infer<typeof VersionDiffSchema>;

export const ItemUsageSchema = z.object({ engagement_count: z.number().int() });
export type ItemUsage = z.infer<typeof ItemUsageSchema>;

// ---------------------------------------------------------------------------
// Curation shapes
// ---------------------------------------------------------------------------

export const DuplicateMatchSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  module_id: z.string(),
  module_name: z.string(),
  subcategory: z.string(),
  text: z.string(),
  score: z.number(),
  exact: z.boolean(),
  /** Why it was flagged (T-0150): exact | wording | meaning. */
  match_kind: z.string().optional(),
  same_module: z.boolean(),
});
export type DuplicateMatch = z.infer<typeof DuplicateMatchSchema>;

export const DraftChangeSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  op: z.string(),
  /** added | reworded | retired | restored */
  change: z.string(),
  subcategory: z.string().nullable(),
  text: z.string().nullable(),
  /** The fields the next version carries for this requirement. */
  metadata: LibraryMetadataSchema.nullable().optional(),
  previous: z
    .object({
      subcategory: z.string(),
      text: z.string(),
      metadata: LibraryMetadataSchema.nullable().optional(),
    })
    .nullable(),
  duplicates: z.array(DuplicateMatchSchema),
  duplicate_decision: z.unknown().nullable(),
  /** Where an added requirement came from (T-0148): curator | spreadsheet | ai_document. */
  source_kind: z.string().nullable().optional(),
  /** Its citation; for `ai_document` see `aiDocumentSource`. */
  source_ref: z.unknown().optional(),
  updated_by: z.string().optional(),
  updated_at: z.string().optional(),
});
export type DraftChange = z.infer<typeof DraftChangeSchema>;

export const DraftViewSchema = z.object({
  module_id: z.string(),
  module_name: z.string(),
  prefix: z.string(),
  current_version: z.number().int(),
  draft_revision: z.number().int(),
  /** The name the module takes when this draft is published, if it is being renamed. */
  pending_name: z.string().nullable().optional(),
  has_changes: z.boolean(),
  counts: z.object({
    added: z.number(),
    reworded: z.number(),
    retired: z.number(),
    renamed: z.number().optional(),
  }),
  unresolved_duplicates: z.number().int(),
  changes: z.array(DraftChangeSchema),
  resulting_item_count: z.number().int(),
});
export type DraftView = z.infer<typeof DraftViewSchema>;

export const CreatedModuleSchema = z.object({
  id: z.string(),
  prefix: z.string(),
  name: z.string(),
  category_id: z.string(),
  status: z.string(),
  current_version: z.number().int(),
  draft_revision: z.number().int(),
});
export type CreatedModule = z.infer<typeof CreatedModuleSchema>;

export const DraftItemResultSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  op: z.string().nullable(),
  changed: z.boolean().optional(),
  subcategory: z.string().optional(),
  text: z.string().optional(),
  draft_revision: z.number().int(),
  duplicates: z.array(DuplicateMatchSchema),
});
export type DraftItemResult = z.infer<typeof DraftItemResultSchema>;

export const DiscardDraftResultSchema = z.object({
  module_id: z.string(),
  discarded: z.number().int(),
  draft_revision: z.number().int(),
});

export const PublishResultSchema = z.object({
  module_id: z.string(),
  version: z.number().int(),
  item_count: z.number().int(),
  added: z.number().int(),
  edited: z.number().int(),
  retired: z.number().int(),
  /** The module's name as published: the new name when the draft renamed it. */
  module_name: z.string().optional(),
  draft_revision: z.number().int(),
});
export type PublishResult = z.infer<typeof PublishResultSchema>;

/** A module after `:retire` or `:restore`. */
export const ModuleStatusResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  category_id: z.string(),
  status: z.string(),
  current_version: z.number().int(),
  draft_revision: z.number().int(),
});
export type ModuleStatusResult = z.infer<typeof ModuleStatusResultSchema>;

export const RenameModuleResultSchema = z.object({
  module_id: z.string(),
  name: z.string(),
  prefix: z.string(),
  pending_name: z.string().nullable(),
  changed: z.boolean(),
  draft_revision: z.number().int(),
});
export type RenameModuleResult = z.infer<typeof RenameModuleResultSchema>;

export const MetadataTidyCandidateSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  subcategory: z.string(),
  /** published | draft: whether the wording being tidied is a pending change. */
  source: z.string(),
  before: z.string(),
  after: z.string(),
  extracted: LibraryMetadataSchema,
  metadata: LibraryMetadataSchema,
  /** Set when the clean wording would repeat another requirement of the module. */
  blocked: z.object({ reason: z.string(), number: z.string() }).nullable(),
});
export type MetadataTidyCandidate = z.infer<typeof MetadataTidyCandidateSchema>;

export const MetadataTidyPreviewSchema = z.object({
  module_id: z.string(),
  module_name: z.string(),
  draft_revision: z.number().int(),
  scanned: z.number().int(),
  candidates: z.array(MetadataTidyCandidateSchema),
  counts: z.object({ found: z.number().int(), blocked: z.number().int() }),
});
export type MetadataTidyPreview = z.infer<typeof MetadataTidyPreviewSchema>;

export const MetadataTidyResultSchema = z.object({
  module_id: z.string(),
  draft_revision: z.number().int(),
  tidied: z.number().int(),
  items: z.array(z.object({ item_id: z.string(), number: z.string() })),
});
export type MetadataTidyResult = z.infer<typeof MetadataTidyResultSchema>;

export const CreatedCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  sort_order: z.number().int(),
});
export type CreatedCategory = z.infer<typeof CreatedCategorySchema>;

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/** `details.reason` of a conflict, or null for any other error. */
export function conflictReason(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  const reason = err.details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}

/** The current draft carried by a `stale_revision` conflict, or null. */
export function staleDraftFromError(err: unknown): DraftView | null {
  if (conflictReason(err) !== 'stale_revision') return null;
  const parsed = DraftViewSchema.safeParse((err as ApiError).details?.['draft']);
  return parsed.success ? parsed.data : null;
}

const ExistingModuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string().optional(),
  status: z.string(),
});
export type ExistingModule = z.infer<typeof ExistingModuleSchema>;

/** The module named by a `module_name_exists` conflict, when the server sent it. */
export function existingModuleFromError(err: unknown): ExistingModule | null {
  if (conflictReason(err) !== 'module_name_exists') return null;
  const parsed = ExistingModuleSchema.safeParse((err as ApiError).details?.['existing_module']);
  return parsed.success ? parsed.data : null;
}

/**
 * True when a `module_name_exists` conflict is about a name another module
 * is being renamed to in an unpublished draft, not a name it already has.
 */
export function isPendingRenameClash(err: unknown): boolean {
  return (
    conflictReason(err) === 'module_name_exists' &&
    (err as ApiError).details?.['pending_rename'] === true
  );
}

/** The number of the requirement an `exact_duplicate` conflict clashes with. */
export function exactDuplicateNumber(err: unknown): string | null {
  if (conflictReason(err) !== 'exact_duplicate') return null;
  const existing = (err as ApiError).details?.['existing'];
  if (existing && typeof existing === 'object') {
    const number = (existing as Record<string, unknown>)['number'];
    if (typeof number === 'string') return number;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

/** The navigation tree. `includeRetired` adds retired categories and modules. */
export function getLibraryTree(includeRetired = false): Promise<LibraryTree> {
  return apiClient(
    BASE,
    LibraryTreeSchema,
    includeRetired ? { query: { include_retired: true } } : {},
  );
}

/**
 * The whole current library in one payload, for client-side search.
 *
 * The route answers `304 Not Modified` when `If-None-Match` carries the
 * current `library_revision` ETag. `apiClient` has no notion of a 304, so
 * this fetcher keeps the last payload with its ETag and replays it. The
 * payload is about 1,400 rows; a publish is the only thing that changes it.
 */
let itemsCache: { etag: string; payload: LibraryItems } | null = null;

/** Test hook: forget the cached full-library payload. */
export function resetLibraryItemsCache(): void {
  itemsCache = null;
}

export async function getLibraryItems(): Promise<LibraryItems> {
  const correlationId =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `cid-${Date.now()}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId,
  };
  const token = await ensureFreshApiToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (itemsCache) headers['If-None-Match'] = itemsCache.etag;

  let res: Response;
  try {
    res = await fetch(`${BASE}/items`, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (res.status === 304 && itemsCache) return itemsCache.payload;

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(
      res.status,
      'internal',
      `Request failed with status ${res.status} and a non-JSON body.`,
      correlationId,
    );
  }
  if (!res.ok) {
    const body = payload as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'internal',
      body?.error?.message ?? `Request failed with status ${res.status}.`,
      correlationId,
    );
  }
  const parsed = LibraryItemsSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      500,
      'schema_mismatch',
      'The API returned an unexpected response shape.',
      correlationId,
    );
  }
  const etag = res.headers.get('etag');
  itemsCache = etag ? { etag, payload: parsed.data } : null;
  return parsed.data;
}

export function getModuleVersions(moduleId: string): Promise<ModuleVersions> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}/versions`,
    ModuleVersionsSchema,
  );
}

export function getModuleContent(moduleId: string, version?: number): Promise<ModuleContent> {
  return apiClient(`${BASE}/modules/${encodeURIComponent(moduleId)}`, ModuleContentSchema, {
    query: { version },
  });
}

export function getVersionDiff(moduleId: string, from: number, to: number): Promise<VersionDiff> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}/versions/${from}..${to}`,
    VersionDiffSchema,
  );
}

export function getItemUsage(itemId: string): Promise<ItemUsage> {
  return apiClient(`${BASE}/items/${encodeURIComponent(itemId)}/usage`, ItemUsageSchema);
}

export function getModuleDraft(moduleId: string): Promise<DraftView> {
  return apiClient(`${BASE}/modules/${encodeURIComponent(moduleId)}/draft`, DraftViewSchema);
}

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface CreateModuleInput {
  name: string;
  category_id: string;
}

export function createModule(input: CreateModuleInput): Promise<CreatedModule> {
  return apiClient(`${BASE}/modules`, CreatedModuleSchema, {
    method: 'POST',
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface AddDraftItemInput {
  moduleId: string;
  subcategory: string;
  text: string;
  baseRevision: number;
}

export function addDraftItem(input: AddDraftItemInput): Promise<DraftItemResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(input.moduleId)}/draft/items`,
    DraftItemResultSchema,
    {
      method: 'POST',
      body: {
        subcategory: input.subcategory,
        text: input.text,
        base_revision: input.baseRevision,
      },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

export interface PatchDraftItemInput {
  moduleId: string;
  itemId: string;
  baseRevision: number;
  text?: string;
  subcategory?: string;
  op?: 'retire' | 'restore';
  /** Item ids this requirement is knowingly kept alongside. */
  keptBothWith?: string[];
}

export function patchDraftItem(input: PatchDraftItemInput): Promise<DraftItemResult> {
  const body: Record<string, unknown> = { base_revision: input.baseRevision };
  if (input.text !== undefined) body['text'] = input.text;
  if (input.subcategory !== undefined) body['subcategory'] = input.subcategory;
  if (input.op !== undefined) body['op'] = input.op;
  if (input.keptBothWith !== undefined) {
    body['duplicate_decision'] = { kept_both_with: input.keptBothWith };
  }
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(input.moduleId)}/draft/items/${encodeURIComponent(input.itemId)}`,
    DraftItemResultSchema,
    { method: 'PATCH', body, idempotencyKey: newIdempotencyKey() },
  );
}

export function discardDraft(moduleId: string, baseRevision: number) {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}/draft`,
    DiscardDraftResultSchema,
    {
      method: 'DELETE',
      query: { base_revision: baseRevision },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

export interface PublishModuleInput {
  moduleId: string;
  changeNote: string;
  baseRevision: number;
}

export function publishModule(input: PublishModuleInput): Promise<PublishResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(input.moduleId)}:publish`,
    PublishResultSchema,
    {
      method: 'POST',
      body: { change_note: input.changeNote, base_revision: input.baseRevision },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

/** Retire a module: hidden from the library and the picker, never deleted. */
export function retireModule(moduleId: string): Promise<ModuleStatusResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}:retire`,
    ModuleStatusResultSchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

/** Bring a retired module back as it was. */
export function restoreModule(moduleId: string): Promise<ModuleStatusResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}:restore`,
    ModuleStatusResultSchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

export interface RenameModuleInput {
  moduleId: string;
  /** The new name, or null to withdraw a pending rename. */
  name: string | null;
  baseRevision: number;
}

/**
 * Propose (or withdraw) a rename in the module's draft. It takes effect
 * when the module is next published; the prefix and numbers never change.
 */
export function renameModule(input: RenameModuleInput): Promise<RenameModuleResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(input.moduleId)}/draft`,
    RenameModuleResultSchema,
    {
      method: 'PATCH',
      body: { name: input.name, base_revision: input.baseRevision },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

/** Each requirement of the module (draft applied) whose wording ends in labelled metadata. */
export function previewMetadataTidy(moduleId: string): Promise<MetadataTidyPreview> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(moduleId)}/draft:tidy-metadata-preview`,
    MetadataTidyPreviewSchema,
    { method: 'POST', body: {} },
  );
}

export interface ApplyMetadataTidyInput {
  moduleId: string;
  itemIds: string[];
  baseRevision: number;
}

/** Move the chosen requirements' fields off their wording, into the module's draft. */
export function applyMetadataTidy(input: ApplyMetadataTidyInput): Promise<MetadataTidyResult> {
  return apiClient(
    `${BASE}/modules/${encodeURIComponent(input.moduleId)}/draft:tidy-metadata`,
    MetadataTidyResultSchema,
    {
      method: 'POST',
      body: { item_ids: input.itemIds, base_revision: input.baseRevision },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

export function createCategory(name: string): Promise<CreatedCategory> {
  return apiClient(`${BASE}/categories`, CreatedCategorySchema, {
    method: 'POST',
    body: { name },
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Retire a category (T-0157): hidden from the tree and from new loads, never
 * deleted. Refused (`category_has_active_modules`) while it has active modules.
 */
export function retireCategory(categoryId: string): Promise<CreatedCategory> {
  return apiClient(
    `${BASE}/categories/${encodeURIComponent(categoryId)}:retire`,
    CreatedCategorySchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

/** Bring a retired category back. */
export function restoreCategory(categoryId: string): Promise<CreatedCategory> {
  return apiClient(
    `${BASE}/categories/${encodeURIComponent(categoryId)}:restore`,
    CreatedCategorySchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export const requirementLibraryKeys = {
  all: ['requirement-library'] as const,
  tree: () => ['requirement-library', 'tree'] as const,
  items: () => ['requirement-library', 'items'] as const,
  versions: (moduleId: string) => ['requirement-library', 'versions', moduleId] as const,
  content: (moduleId: string, version: number | undefined) =>
    ['requirement-library', 'content', moduleId, version ?? 'current'] as const,
  diff: (moduleId: string, from: number, to: number) =>
    ['requirement-library', 'diff', moduleId, from, to] as const,
  usage: (itemId: string) => ['requirement-library', 'usage', itemId] as const,
  draft: (moduleId: string) => ['requirement-library', 'draft', moduleId] as const,
  tidy: (moduleId: string) => ['requirement-library', 'tidy', moduleId] as const,
};

/**
 * The navigation tree. With `includeRetired` it also carries retired
 * categories and modules, under its own cache key (which `tree()` is a
 * prefix of, so one invalidation refreshes both).
 */
export function useLibraryTreeQuery(includeRetired = false) {
  return useQuery({
    queryKey: includeRetired
      ? ([...requirementLibraryKeys.tree(), 'with-retired'] as const)
      : requirementLibraryKeys.tree(),
    queryFn: () => getLibraryTree(includeRetired),
    staleTime: 30_000,
    // Toggling "Show retired" must not blank the screen while it loads.
    placeholderData: (prev) => prev,
  });
}

export function useLibraryItemsQuery() {
  return useQuery({
    queryKey: requirementLibraryKeys.items(),
    queryFn: getLibraryItems,
    // Revalidation is cheap (a 304), so keep the payload fresh.
    staleTime: 30_000,
  });
}

export function useModuleVersionsQuery(moduleId: string | null | undefined) {
  return useQuery({
    queryKey: requirementLibraryKeys.versions(moduleId ?? ''),
    queryFn: () => getModuleVersions(moduleId!),
    enabled: Boolean(moduleId),
    staleTime: 30_000,
  });
}

export function useModuleContentQuery(moduleId: string | null, version: number | null) {
  return useQuery({
    queryKey: requirementLibraryKeys.content(moduleId ?? '', version ?? undefined),
    queryFn: () => getModuleContent(moduleId!, version ?? undefined),
    enabled: Boolean(moduleId) && version != null,
    // A published version never changes.
    staleTime: Infinity,
  });
}

export function useVersionDiffQuery(
  moduleId: string | null,
  from: number | null,
  to: number | null,
) {
  return useQuery({
    queryKey: requirementLibraryKeys.diff(moduleId ?? '', from ?? 0, to ?? 0),
    queryFn: () => getVersionDiff(moduleId!, from!, to!),
    enabled: Boolean(moduleId) && from != null && to != null && from !== to,
    staleTime: Infinity,
  });
}

export function useItemUsageQuery(itemId: string | null | undefined) {
  return useQuery({
    queryKey: requirementLibraryKeys.usage(itemId ?? ''),
    queryFn: () => getItemUsage(itemId!),
    enabled: Boolean(itemId),
    staleTime: 60_000,
  });
}

/** The tidy preview, read fresh each time the sheet opens. */
export function useMetadataTidyPreviewQuery(moduleId: string | null) {
  return useQuery({
    queryKey: requirementLibraryKeys.tidy(moduleId ?? ''),
    queryFn: () => previewMetadataTidy(moduleId!),
    enabled: Boolean(moduleId),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useModuleDraftQuery(moduleId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: requirementLibraryKeys.draft(moduleId ?? ''),
    queryFn: () => getModuleDraft(moduleId!),
    enabled: enabled && Boolean(moduleId),
    // Another curator may be editing the same draft.
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/**
 * After a draft write: move the cached draft to the revision the server
 * returned straight away (so the next write is not stale), then refetch
 * the draft for the full change list and the tree for its draft markers.
 */
export function afterDraftWrite(qc: QueryClient, moduleId: string, draftRevision: number): void {
  qc.setQueryData<DraftView>(requirementLibraryKeys.draft(moduleId), (prev) =>
    prev ? { ...prev, draft_revision: draftRevision } : prev,
  );
  void qc.invalidateQueries({ queryKey: requirementLibraryKeys.draft(moduleId) });
  void qc.invalidateQueries({ queryKey: requirementLibraryKeys.tree() });
}

/** On a stale revision, replace the cached draft with the one the server sent. */
export function adoptStaleDraft(qc: QueryClient, moduleId: string, err: unknown): void {
  const draft = staleDraftFromError(err);
  if (draft) qc.setQueryData(requirementLibraryKeys.draft(moduleId), draft);
  else if (conflictReason(err) === 'stale_revision') {
    void qc.invalidateQueries({ queryKey: requirementLibraryKeys.draft(moduleId) });
  }
}

export function useCreateModuleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createModule,
    onSuccess: () => qc.invalidateQueries({ queryKey: requirementLibraryKeys.tree() }),
  });
}

/** Retiring or restoring changes what everyone sees: refetch the lot. */
export function useSetModuleStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { moduleId: string; action: 'retire' | 'restore' }) =>
      input.action === 'retire' ? retireModule(input.moduleId) : restoreModule(input.moduleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: requirementLibraryKeys.all }),
  });
}

export function useRenameModuleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: renameModule,
    onSuccess: (data, input) => {
      qc.setQueryData<DraftView>(requirementLibraryKeys.draft(input.moduleId), (prev) =>
        prev ? { ...prev, pending_name: data.pending_name } : prev,
      );
      afterDraftWrite(qc, input.moduleId, data.draft_revision);
    },
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}

/** Retiring or restoring a category changes what everyone sees: refetch the lot. */
export function useSetCategoryStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { categoryId: string; action: 'retire' | 'restore' }) =>
      input.action === 'retire'
        ? retireCategory(input.categoryId)
        : restoreCategory(input.categoryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: requirementLibraryKeys.all }),
  });
}

export function useCreateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: requirementLibraryKeys.tree() }),
  });
}

export function useAddDraftItemMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: addDraftItem,
    onSuccess: (data, input) => afterDraftWrite(qc, input.moduleId, data.draft_revision),
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}

export function usePatchDraftItemMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: patchDraftItem,
    onSuccess: (data, input) => afterDraftWrite(qc, input.moduleId, data.draft_revision),
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}

export function useApplyMetadataTidyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: applyMetadataTidy,
    onSuccess: (data, input) => afterDraftWrite(qc, input.moduleId, data.draft_revision),
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}

export function useDiscardDraftMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { moduleId: string; baseRevision: number }) =>
      discardDraft(input.moduleId, input.baseRevision),
    onSuccess: (data, input) => afterDraftWrite(qc, input.moduleId, data.draft_revision),
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}

export function usePublishModuleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishModule,
    // Publishing changes everything published: refetch the lot.
    onSuccess: () => qc.invalidateQueries({ queryKey: requirementLibraryKeys.all }),
    onError: (err, input) => adoptStaleDraft(qc, input.moduleId, err),
  });
}
